/* eslint-disable @typescript-eslint/no-explicit-any */
import { requestJira } from '../../shims/forge-bridge';

import Strings from '../../common/Strings';
import Routes, { BASE_JIRA_URL } from '../../common/Routes';
import {
  CreateBulkIssuesResponse,
  CreateBulkEpicsResponse,
  Epic,
  JiraProject,
  ServerError,
  EpicToCreatedJiraEpicIdMap,
  EpicsMap,
  StoriesMap,
  JiraIssue,
  PromiseStatus,
  CreateBulkStoriesResponse,
  Story,
  PublishJiraResponse,
  CreateEpicAndStoriesResponse,
  JiraError
} from '../../common/Types';
import { ISSUE_PUBLISH_COUNT_THRESHOLD, MAX_EPICS_TO_LOAD } from '../../common/Constants';
import {
  getCreateEpicsRequestBody,
  getCreateStoriesRequestBody,
  isEpicsValid,
  isStoriesValid,
  throwExceptionIfIssueTypesInvalid,
  getPublishError
} from '../../utils/jiraUtils';

export type JiraCreateStoryBody = {
  id: string;
  description: string;
  summary: string;
  epic_id?: string;
  jiraEpicId?: string;
};

export interface IJiraApiClient {
  loadEpics(currentProject: JiraProject): Promise<EpicsMap | ServerError>;
  searchExistingEpics(currentProject: JiraProject, ids: string[]): Promise<Epic[]>;
  getRecentProject(): Promise<JiraProject | ServerError>;
  publishEpicsAndStoriesToJira(
    epics: EpicsMap,
    jiraEpics: EpicsMap,
    stories: StoriesMap,
    currentProject: JiraProject
  ): Promise<PublishJiraResponse>;
}

export type EpicResponse = {
  expand: string;
  id: string;
  self: string;
  key: string;
  fields: {
    summary: string;
    description?: string;
  };
};

export class JiraApiClient implements IJiraApiClient {
  async getIssueTypes(currentProject: JiraProject): Promise<string[]> {
    const url = `${BASE_JIRA_URL}${Routes.GET_PROJECT}/${currentProject.id}`;

    // Get the response and parse it as JSON
    const response = await requestJira(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    // Parse the response body as JSON
    const data: JiraProject & { issueTypes: JiraIssue[] } = await response.json();

    // Map the issue types to their names
    return data.issueTypes.map((issue) => issue.name);
  }

  async loadEpics(currentProject: JiraProject): Promise<EpicsMap | ServerError> {
    const epicsFromJira: EpicsMap = {};
    let nextPageToken: string | undefined;
    let isLast = false;

    try {
      // Loop through all pages until we get all epics
      while (!isLast) {
        const bodyData: any = {
          jql: `issueType = Epic AND project = '${currentProject.key}'`,
          maxResults: MAX_EPICS_TO_LOAD,
          fields: ['summary', 'description']
        };

        // Add pagination token if we have one
        if (nextPageToken) {
          bodyData.nextPageToken = nextPageToken;
        }

        const url = `${BASE_JIRA_URL}${Routes.LOAD_EPICS}`;
        const response = await requestJira(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(bodyData),
        });

        const data = await response.json();

        // Check if the response has error messages
        if (data.errorMessages && data.errorMessages.length > 0) {
          console.error("JIRA API error:", data.errorMessages);
          return { message: `JIRA API error: ${data.errorMessages.join(', ')}` } as ServerError;
        }

        // Check if issues exist
        if (!data.issues) {
          console.error("No issues in response:", data);
          return { message: 'No issues found in JIRA response' } as ServerError;
        }

        // Process the epics from this page
        data.issues.forEach((epic: EpicResponse) => {
          epicsFromJira[epic.id] = {
            id: epic.id,
            description: epic.fields.description ?? epic.fields.summary,
            summary: epic.fields.summary,
            isNew: false,
            key: epic.key,
            jiraId: epic.id
          };
        });

        // Update pagination variables
        isLast = data.isLast !== false; // Handle case where isLast might be undefined
        nextPageToken = data.nextPageToken;

      }
      return epicsFromJira;
    } catch (e) {
      console.error("Error loading epics from Jira:", e);
      return { message: 'Error loading the epics from Jira' } as ServerError;
    }
  }

  async searchExistingEpics(currentProject: JiraProject, epicIds: string[]): Promise<Epic[]> {
    if (epicIds.length === 0) {
      return [];
    }

    const allEpics: Epic[] = [];
    let nextPageToken: string | undefined;
    let isLast = false;

    try {
      // Loop through all pages until we get all epics
      while (!isLast) {
        const bodyData: any = {
          jql: `issueKey in (${epicIds.join(', ')}) AND project = '${currentProject.key}'`,
          fields: ['id', 'summary', 'key']
        };

        // Add pagination token if we have one
        if (nextPageToken) {
          bodyData.nextPageToken = nextPageToken;
        }

        const url = `${BASE_JIRA_URL}${Routes.SEARCH}`;
        const response = await requestJira(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(bodyData),
        });

        const data = await response.json();
        
        // Add epics from this page to our collection
        if (data.issues) {
          allEpics.push(...data.issues);
        }

        // Update pagination variables
        isLast = data.isLast !== false; // Handle case where isLast might be undefined
        nextPageToken = data.nextPageToken;

      }
      return allEpics;
    } catch (e) {
      console.error("Error fetching epics:", e);
      return [];
    }
  }

  async getRecentProject(): Promise<JiraProject | ServerError> {
    try {
      const url  = Routes.GET_RECENT_PROJECT;
      const fq_url = `${BASE_JIRA_URL}${url}`;
      const response = await requestJira(fq_url, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });
      const data: JiraProject[] = await response.json();
      return data[0];
    } catch (e) {
      console.error("Error fetching recent project:", e); // Log error
      return { message: 'Error loading the recent projects from Jira' } as ServerError;
    }
  }

  async publishEpicsToJira(
    epics: Epic[],
    currentProject: JiraProject
  ): Promise<CreateBulkEpicsResponse> {
    const createEpicsRequestBody = getCreateEpicsRequestBody(epics, currentProject);

    try {
      const url = `${BASE_JIRA_URL}${Routes.BULK_CREATE_ISSUES}`;
      const response = await requestJira(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(createEpicsRequestBody),
      });

      const data: CreateBulkIssuesResponse = await response.json();

      return {
        items: epics,
        ...data
      };
    } catch (error) {
      console.error("Error publishing epics to Jira:", error); // Log error

      return {
        items: epics,
        issues: [],
        errors: getPublishError(error, epics)
      };
    }
  }

  async publishStoriesToJira(
    stories: Story[],
    currentProject: JiraProject
  ): Promise<CreateBulkStoriesResponse> {
    const createStoriesRequestBody = getCreateStoriesRequestBody(stories, currentProject);

    try {
      const url = `${BASE_JIRA_URL}${Routes.BULK_CREATE_ISSUES}`;
      const response = await requestJira(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(createStoriesRequestBody),
      });

      const data: CreateBulkIssuesResponse = await response.json();

      return {
        items: stories,
        ...data
      };
    } catch (error) {
      console.error("Error publishing stories to Jira:", error); // Log error
      return {
        items: stories,
        issues: [],
        errors: getPublishError(error, stories)
      };
    }
  }

  private getEpicList(epic: EpicsMap): Epic[] {
    // All epics in the epics map are new/unpublished by design
    // (existing Jira epics are stored separately in jiraEpics)
    return Object.keys(epic).map((epicId) => {
      return { ...epic[epicId] };
    });
  }

  private getStoryList(story: StoriesMap): JiraCreateStoryBody[] {
    const storyList = Object.keys(story).map((storyId) => {
      return { ...story[storyId] };
    });
    return storyList;
  }

  private getBatchItemByIndex(index: number, batchSize: number, epics: Epic[] | Story[]) {
    const startIndex = index * batchSize;
    const endIndex = startIndex + batchSize;
    return epics.slice(startIndex, endIndex);
  }

  private async publishItemInBatches(
    items: Epic[] | Story[],
    currentProject: JiraProject,
    functionName: (
      arg1: Epic[] | Story[],
      arg2: JiraProject
    ) => Promise<CreateBulkStoriesResponse | CreateBulkEpicsResponse>
  ): Promise<CreateEpicAndStoriesResponse> {
    const batchSize = ISSUE_PUBLISH_COUNT_THRESHOLD; // Maximum number of epics per request
    const totalItems = items.length;
    const totalBatches = Math.ceil(totalItems / batchSize);
    const itemResult: CreateEpicAndStoriesResponse = {
      success: [],
      failed: [],
      failedReason: {}
    };
    const promiseList = [];
    for (let i = 0; i < totalBatches; i++) {
      promiseList.push(functionName(this.getBatchItemByIndex(i, batchSize, items), currentProject));
    }

    const results = await Promise.allSettled(promiseList);
    results.forEach((result, index) => {
      const batchItems = this.getBatchItemByIndex(index, batchSize, items);
      if (result.status === PromiseStatus.SUCCESS) {
        // handle individual item failure
        itemResult.failed = [
          ...itemResult.failed,
          ...result.value.errors.map((error) => batchItems[error.failedElementNumber].id)
        ];
        itemResult.failedReason = {
          ...itemResult.failedReason,
          ...result.value.errors.reduce(
            (pv: Record<string, number>, cv: JiraError) => ({
              ...pv,
              [batchItems[cv.failedElementNumber].id]: +cv.status
            }),
            {}
          )
        };

        const publishedItems = (batchItems as (Epic | Story)[]).filter(
          (item: Epic | Story) => !itemResult.failed.includes(item.id)
        );
        itemResult.success = [
          ...itemResult.success,
          ...publishedItems.map((item, i) => ({
            id: item.id,
            jiraId: result.value.issues[i].id
          }))
        ];
      }
    });
    return itemResult;
  }

  private mapEpicsToStories(
    stories: Story[],
    jiraEpics: EpicsMap,
    epicResponse: CreateEpicAndStoriesResponse
  ): Story[] {
    const createdEpics: EpicToCreatedJiraEpicIdMap = {};
    epicResponse.success.forEach((epic) => {
      createdEpics[epic.id] = epic.jiraId;
    });
    return stories.map((story) => {
      const jiraEpicId = story.epic_id && jiraEpics
        ? jiraEpics[story.epic_id]
          ? story.epic_id
          : createdEpics[story.epic_id]
        : undefined;
      return { ...story, jiraEpicId };
    });
  }

  async publishEpicsAndStoriesToJira(
    epics: EpicsMap,
    jiraEpics: EpicsMap,
    stories: StoriesMap,
    currentProject: JiraProject
  ): Promise<PublishJiraResponse> {
    const epicList = this.getEpicList(epics);
    const storyList = this.getStoryList(stories);

    const issueTypes = await this.getIssueTypes(currentProject);
    throwExceptionIfIssueTypesInvalid(epicList, storyList, issueTypes);
    if (!isEpicsValid(epicList) || !isStoriesValid(storyList)) {
      throw new Error(Strings.ERROR_MESSAGES.INVALID_EPIC_OR_STORY);
    }

    const publishedEpics = await this.publishItemInBatches(
      epicList,
      currentProject,
      this.publishEpicsToJira
    );
    let publishedStories: CreateEpicAndStoriesResponse = {
      success: [],
      failed: [...Object.keys(stories)],
      failedReason: {}
    };
    if (publishedEpics.failed.length === 0) {
      const mappedStories = this.mapEpicsToStories(storyList, jiraEpics, publishedEpics);
      // Publish story only when all epics are success
      publishedStories = await this.publishItemInBatches(
        mappedStories,
        currentProject,
        this.publishStoriesToJira
      );
    }
    return {
      epics: publishedEpics,
      stories: publishedStories
    };
  }
}
